import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect } from "react";
import { Box, Divider, Flex, HStack, Icon, IconButton, Spinner, Text, Tooltip, VStack } from "@chakra-ui/react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import embed, { VegaView } from "vega-embed";
import { AppIcons } from "constants/icons";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { DataFrameAnalysisProto } from "build-ts/dataframe";
import {
  getArtifactKind,
  getArtifactOpenOpts,
  getArtifactType,
  vegaViewToDataURL,
} from "modules/artifacts/utils/artifactUtils";
import { triggerDownload } from "utils/helperFuncUtils";
import showToast from "hooks/useCustomToast";
import { ReportDownloadFormat } from "services/serviceUtils";
import { ArtifactOpenOpts } from "modules/interfaces";
import ArtifactPanelHeader from "modules/artifacts/ArtifactPanelHeader";
import PandasTable from "./PandasTable";
import { copyRawTableToClipboard, downloadRawTableAsExcel } from "../utils/pandasTableUtils";
import {
  TabId,
  RESULT_PILL_KIND,
  CODE_TAB_GUTTER_SLOT_W,
  isHashCommentLine,
  stripHashCommentDisplay,
  splitPythonIntoCommentLedSections,
  countCodeLines,
  isPythonPrologueSection,
  splitIntoDisplayLines,
  TAB_ID,
  PYTHON_CODE_STYLE,
} from "../utils/dataFrameCodeUtils";
import { extractFormattedDataFromPandasDf, getPandasDfRowColCount } from "../utils/pandasTableUtils";
import LineageGraphTab from "./LineageGraphTab";
import { InputsTab } from "./InputsTab";
import { DownloadMenu } from "../../../common/DownloadMenu";

// PDF.js does heavy parsing in a Web Worker to avoid blocking the UI thread.
// CRA's Webpack does not handle new URL(..., import.meta.url) for worker files,
// so we serve the worker from the public/ folder (copied there by the prebuild script)
// and reference it via PUBLIC_URL so it works at any deployment sub-path.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
SyntaxHighlighter.registerLanguage("python", python);

// ── Module-level constants (never change, no reason to recreate per render) ─

const SCROLL_SX = {
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
} as const;

const CHART_SCROLL_SX = {
  "&::-webkit-scrollbar": { height: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
} as const;

const CODE_SCROLL_SX = {
  "&::-webkit-scrollbar": { width: "4px", height: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
} as const;

// ── Private sub-components ──────────────────────────────────────────────────

const CodeSnippetBody: React.FC<{ source: string }> = ({ source }) => (
  <Box as="code" display="block" color="gray.800" fontSize="inherit" fontFamily="inherit">
    {splitIntoDisplayLines(source).map((line, i) => {
      const bold = isHashCommentLine(line);
      const shown = stripHashCommentDisplay(line);
      return (
        <Text
          key={i}
          as="span"
          display="block"
          fontWeight={bold ? "700" : "500"}
          color="inherit"
          mb={0}
          whiteSpace="pre-wrap"
          lineHeight="2"
        >
          {shown.length === 0 ? "\u00a0" : shown}
        </Text>
      );
    })}
  </Box>
);

type CodeView = "blocks" | "code";

const CodeTab: React.FC<{ code?: string }> = ({ code }) => {
  const sections = useMemo(() => (code?.trim() ? splitPythonIntoCommentLedSections(code) : []), [code]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [view, setView] = useState<CodeView>("blocks");
  // Blocks view is only meaningful when at least one section has a comment header.
  // If the code has no comments at all, the two views would be identical, so we
  // hide the toggle and always render the code view.
  const hasAnyComments = useMemo(() => sections.some(sec => sec.comment.length > 0), [sections]);

  if (!code?.trim()) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No code available.
        </Text>
      </Box>
    );
  }

  const toggleSection = (i: number) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }));

  return (
    <Box
      m={0}
      w="100%"
      px={2}
      pt={0}
      pb={2}
      overflowX="auto"
      fontSize="xs"
      lineHeight="1.65"
      textAlign="left"
      bg="white"
      sx={CODE_SCROLL_SX}
    >
      {/* View switcher — only shown when there are comment-led sections to display */}
      {hasAnyComments && (
        <Flex justify="flex-end" position="sticky" top={0} zIndex={10} bg="white" py={1} mb={0}>
          <HStack spacing={0} borderRadius="md" border="1px solid" borderColor="gray.500" overflow="hidden">
            <Tooltip label="Blocks view" hasArrow placement="top">
              <IconButton
                h="24px"
                w="26px"
                minW="20px"
                variant="ghost"
                borderRadius={0}
                aria-label="Blocks view"
                icon={<Icon as={AppIcons.blocks} boxSize={3} />}
                bg={view === "blocks" ? "purple.100" : "white"}
                color={view === "blocks" ? "purple.700" : "gray.600"}
                _hover={{ bg: "purple.50", color: "purple.600" }}
                onClick={() => setView("blocks")}
              />
            </Tooltip>
            <Box w="1px" h="24px" bg="gray.500" flexShrink={0} />
            <Tooltip label="Code view" hasArrow placement="top">
              <IconButton
                h="24px"
                w="26px"
                minW="20px"
                variant="ghost"
                borderRadius={0}
                aria-label="Code view"
                icon={<Icon as={AppIcons.codeAlt} boxSize={3} />}
                bg={view === "code" ? "purple.100" : "white"}
                color={view === "code" ? "purple.700" : "gray.600"}
                _hover={{ bg: "purple.50", color: "purple.600" }}
                onClick={() => setView("code")}
              />
            </Tooltip>
          </HStack>
        </Flex>
      )}

      {/* Raw Python — full syntax highlighting via PrismLight (Python only bundle) */}
      {(view === "code" || !hasAnyComments) && (
        <SyntaxHighlighter
          language="python"
          style={PYTHON_CODE_STYLE}
          wrapLongLines
          customStyle={{
            margin: 0,
            padding: 0,
            background: "transparent",
            fontSize: "13px",
            lineHeight: "inherit",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowX: "visible",
            border: "none",
            maxWidth: "100%",
          }}
          codeTagProps={{ style: { fontFamily: "inherit" } }}
        >
          {code ?? ""}
        </SyntaxHighlighter>
      )}
      {/* Grouped comment-led collapsible sections */}
      {view === "blocks" && hasAnyComments && (
        <VStack
          align="stretch"
          spacing={2}
          pt={0}
          divider={<Divider borderColor="gray.100" my={0} />}
          fontFamily="mono"
        >
          {sections.map((sec, i) => {
            const commentLines = sec.comment.length ? sec.comment.split("\n") : [];
            const lineCount = countCodeLines(sec.code);
            const hasCodeBlock = lineCount > 0;
            const isOpen = expanded[i] ?? false;

            if (!hasCodeBlock) {
              return (
                <Box key={i}>
                  {commentLines.length > 0 && (
                    <Box>
                      {commentLines.map((line, li) => {
                        const bold = isHashCommentLine(line);
                        const shown = stripHashCommentDisplay(line);
                        return (
                          <Text
                            key={li}
                            as="span"
                            display="block"
                            fontWeight={bold ? "700" : "400"}
                            color="gray.800"
                            mb={0}
                            whiteSpace="pre-wrap"
                          >
                            {shown.length === 0 ? "\u00a0" : shown}
                          </Text>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            }

            if (isPythonPrologueSection(sec, i)) {
              return (
                <Box key={i}>
                  <HStack align="flex-start" spacing={2}>
                    <Box flexShrink={0} w={CODE_TAB_GUTTER_SLOT_W} aria-hidden />
                    <Box flex="1" minW={0}>
                      <Box
                        as="pre"
                        m={0}
                        p={0}
                        overflowX="auto"
                        fontFamily="mono"
                        fontSize="inherit"
                        sx={CHART_SCROLL_SX}
                      >
                        <CodeSnippetBody source={sec.code} />
                      </Box>
                    </Box>
                  </HStack>
                </Box>
              );
            }

            return (
              <Box key={i}>
                <HStack align="flex-start" spacing={2} mb={isOpen ? 0 : 1}>
                  <Box flexShrink={0} w={CODE_TAB_GUTTER_SLOT_W} display="flex" justifyContent="center" pt={0.5}>
                    <IconButton
                      size="2xs"
                      variant="ghost"
                      bg="purple.50"
                      color="purple.700"
                      _hover={{ bg: "purple.100", color: "purple.700" }}
                      _active={{ bg: "purple.200" }}
                      aria-label={isOpen ? "Hide code under this comment" : "Show code under this comment"}
                      aria-expanded={isOpen}
                      icon={<Icon as={isOpen ? AppIcons.minus : AppIcons.plus} boxSize={3} color="purple.700" />}
                      onClick={() => toggleSection(i)}
                    />
                  </Box>
                  <Box flex="1" minW={0}>
                    {commentLines.length > 0 && (
                      <Box mb={0}>
                        {commentLines.map((line, li) => {
                          const bold = isHashCommentLine(line);
                          const shown = stripHashCommentDisplay(line);
                          return (
                            <Text
                              key={li}
                              as="span"
                              display="block"
                              fontWeight={bold ? "600" : "400"}
                              color="gray.800"
                              mb={0}
                              whiteSpace="pre-wrap"
                              fontSize="0.8rem"
                            >
                              {shown.length === 0 ? "\u00a0" : shown}
                            </Text>
                          );
                        })}
                      </Box>
                    )}
                    {!isOpen && (
                      <Text fontSize="xs" color="gray.600" mb={0} mt={commentLines.length > 0 ? 1 : 0}>
                        ({lineCount} line{lineCount === 1 ? "" : "s"})
                      </Text>
                    )}
                  </Box>
                </HStack>
                <HStack align="flex-start" spacing={2} mt={0} pt={0}>
                  <Box flexShrink={0} w={CODE_TAB_GUTTER_SLOT_W} aria-hidden />
                  <Box flex="1" minW={0} pt={0}>
                    {isOpen && (
                      <Box
                        as="pre"
                        m={0}
                        p={0}
                        overflowX="auto"
                        fontFamily="mono"
                        fontSize="inherit"
                        sx={CHART_SCROLL_SX}
                      >
                        <CodeSnippetBody source={sec.code} />
                      </Box>
                    )}
                  </Box>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export const AnalysisChartTab: React.FC<{
  chartVegaSpecJson?: string;
  onViewReady?: (view: VegaView) => void;
}> = ({ chartVegaSpecJson, onViewReady }) => {
  const chartContainerRef = React.useRef<HTMLDivElement | null>(null);
  const measureRef = React.useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = React.useState<number | null>(null);
  // Keep a stable ref so the effect doesn't re-run when the parent re-renders.
  const onViewReadyRef = React.useRef(onViewReady);
  onViewReadyRef.current = onViewReady;

  // useLayoutEffect runs after DOM mutations but before paint, so getBoundingClientRect()
  // reflects the final layout on the first render — no flash of zero-width chart.
  // Store the raw available width here; gutter is subtracted in the embed effect
  // where we know which legend orientation will be used.
  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        const w = Math.floor(entry.contentRect.width);
        if (w > 0) setContainerWidth(w);
      }
    });
    observer.observe(el);
    const initial = Math.floor(el.getBoundingClientRect().width);
    if (initial > 0) setContainerWidth(initial);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (containerWidth === null || containerWidth === 0) return;
    let finalized: (() => void) | undefined;
    const render = async () => {
      if (!chartContainerRef.current || !chartVegaSpecJson) return;
      try {
        const parsed = JSON.parse(chartVegaSpecJson) as Record<string, unknown>;
        const parsedConfig = (parsed.config ?? {}) as Record<string, unknown>;
        const parsedLegend = (parsedConfig.legend ?? {}) as Record<string, unknown>;
        // Compound and faceted specs must NOT receive a top-level width override:
        // Vega-Lite propagates it into every sub-chart / facet column, producing
        // enormous layouts (width × n_facets) that render off-screen as nothing.
        // These specs scroll horizontally with their own hardcoded per-facet widths.
        // Covers: vconcat/hconcat/concat (explicit compound), top-level `facet`,
        // and encoding.column / encoding.row (implicit faceting via encoding shorthand).
        const parsedEncoding = (parsed.encoding ?? {}) as Record<string, unknown>;
        const isCompoundSpec =
          "vconcat" in parsed ||
          "hconcat" in parsed ||
          "concat" in parsed ||
          "facet" in parsed ||
          "column" in parsedEncoding ||
          "row" in parsedEncoding;

        // Scale height proportionally to the container width so the chart maintains
        // its intended aspect ratio as the panel resizes. Falls back to a 3:5
        // (height:width) ratio when the spec lacks explicit dimensions.
        // Cap at 75% of the viewport width so charts don't sprawl on very wide panels.
        // window.innerWidth is read fresh here because this effect re-runs on every
        // containerWidth change (driven by ResizeObserver), so it's always current.
        const effectiveWidth = Math.min(containerWidth, window.innerWidth * 0.75);
        const specWidth = typeof parsed.width === "number" ? parsed.width : null;
        const specHeight = typeof parsed.height === "number" ? parsed.height : null;
        const aspectRatio = specWidth && specHeight ? specHeight / specWidth : 0.6;
        const derivedHeight = Math.round(effectiveWidth * aspectRatio);

        // Known Vega bug: resolve.scale.y = "independent" on a LayerSpec nested inside a
        // FacetSpec generates internal scale names ("child_layer_0_y") that Vega cannot
        // resolve when building axis configs. Strip it here for already-stored specs so
        // the chart renders with a shared y-axis rather than crashing silently.
        if ("facet" in parsed) {
          const innerSpec = parsed.spec as Record<string, unknown> | undefined;
          if (innerSpec && "layer" in innerSpec && innerSpec.resolve) {
            const resolveMap = innerSpec.resolve as Record<string, unknown>;
            const scaleResolve = resolveMap.scale as Record<string, unknown> | undefined;
            if (scaleResolve?.y === "independent") {
              delete scaleResolve.y;
              if (Object.keys(scaleResolve).length === 0) delete resolveMap.scale;
              if (Object.keys(resolveMap).length === 0) delete innerSpec.resolve;
            }
          }
        }

        // For simple charts: constrain total width to the container and derive a
        // proportional height. For compound / faceted specs: skip these overrides —
        // injecting a top-level width multiplies across every sub-chart / facet
        // column, producing enormous layouts that collapse the SVG viewport.
        //
        // Also skip legend-config injection for compound specs: config.legend.columns
        // is not a valid LegendConfig property (it belongs on per-encoding legend),
        // so injecting it causes vega-embed to fail schema validation silently.
        const spec = isCompoundSpec
          ? { ...parsed }
          : {
              ...parsed,
              width: effectiveWidth,
              height: derivedHeight,
              autosize: { type: "fit-x", contains: "padding" },
              config: {
                ...parsedConfig,
                legend: {
                  ...parsedLegend,
                  // Limit columns so items wrap to multiple rows instead of overflowing.
                  // Each legend entry is ~100px wide on average; cap at a minimum of 3.
                  columns: Math.max(3, Math.floor(effectiveWidth / 100)),
                },
              },
            };
        const result = await embed(chartContainerRef.current, spec, {
          actions: false,
          renderer: "svg",
        });
        finalized = result.finalize;
        onViewReadyRef.current?.(result.view);
      } catch {
        // Keep chart rendering resilient if payload is malformed.
        showToast("Chart failed to render", "The chart specification could not be displayed.", "error");
      }
    };
    render();
    return () => {
      if (finalized) finalized();
    };
  }, [chartVegaSpecJson, containerWidth]);

  if (!chartVegaSpecJson) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No chart available.
        </Text>
      </Box>
    );
  }
  return (
    <Box px={2} py={2}>
      {/* measureRef on an unpadded div — getBoundingClientRect().width equals the
          chart's available content width with no padding offset to subtract. */}
      <Box ref={measureRef} w="100%">
        {/* overflowX="auto" lets compound / faceted charts scroll horizontally
            instead of being clipped by ancestor overflow:hidden containers. */}
        <Box ref={chartContainerRef} overflowX="auto" />
      </Box>
    </Box>
  );
};

// ── DocxTab ─────────────────────────────────────────────────────────────────

const DocxTab: React.FC<{
  report: DataFrameAnalysisProto;
  reportIndex: number;
}> = ({ report, reportIndex }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [previewWidth, setPreviewWidth] = useState<number>(600);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setPreviewWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const reportProto = report.reports?.[reportIndex];
  const hasPdf = !!(reportProto?.pdf_bytes && reportProto.pdf_bytes.length > 0);

  const pdfBlobUrl = useMemo(() => {
    if (!hasPdf) return null;
    const plain = new Uint8Array(reportProto!.pdf_bytes!).buffer as ArrayBuffer;
    const blob = new Blob([plain], { type: "application/pdf" });
    return URL.createObjectURL(blob);
  }, [reportProto?.pdf_bytes, hasPdf]);

  return (
    <Box ref={containerRef} px={2} py={2}>
      {hasPdf && pdfBlobUrl ? (
        <>
          <Document
            file={pdfBlobUrl}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={
              <HStack justify="center" py={8} spacing={2}>
                <Spinner size="sm" color="gray.400" />
                <Text fontSize="sm" color="gray.400" mb={0}>
                  Loading preview…
                </Text>
              </HStack>
            }
            error={
              <Text fontSize="sm" color="red.400" px={3} py={4} mb={0}>
                Could not render preview.
              </Text>
            }
          >
            {Array.from({ length: numPages ?? 0 }, (_, i) => (
              <Box key={i + 1} mb={2}>
                <Page pageNumber={i + 1} width={previewWidth || undefined} renderAnnotationLayer renderTextLayer />
              </Box>
            ))}
          </Document>
          {numPages && (
            <Text fontSize="xs" color="gray.400" mt={1} mb={0}>
              {numPages} page{numPages !== 1 ? "s" : ""}
            </Text>
          )}
        </>
      ) : (
        <Box px={4} py={6} textAlign="center">
          <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
            Preview not available — use the download button to regenerate.
          </Text>
        </Box>
      )}
    </Box>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

type ResultPill = { kind: (typeof RESULT_PILL_KIND)[keyof typeof RESULT_PILL_KIND]; index: number; name: string };

export interface DataFrameAnalysisProps {
  data: DataFrameAnalysisProto;
  activeTab: TabId;
  selectedResultPillName: string;
  /** Persisted selected input name (parent_header.name) — drives inputsIndex. */
  selectedInputName?: string;
  onTabChange: (tab: TabId) => void;
  onResultPillChange: (name: string) => void;
  /** Called when the user selects a different input so the container can persist it. */
  onInputPillChange?: (name: string) => void;
  lookupFn?: (name: string) => ArtifactProto | undefined;
  onOpenArtifact?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  onPopulateInput?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => Promise<void> | void;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  onDownloadReport?: (format: ReportDownloadFormat, reportName?: string) => Promise<void> | void;
  /** When true, suppress the ArtifactPanelHeader — used when embedded inside another card. */
  hideHeader?: boolean;
}

/**
 * Pure UI component for DataFrameAnalysis artifacts.
 * All tab and selection state is owned by containers/DataFrameAnalysis.tsx.
 */
const DataFrameAnalysis: React.FC<DataFrameAnalysisProps> = ({
  data,
  activeTab,
  selectedResultPillName,
  selectedInputName,
  onTabChange,
  onResultPillChange,
  onInputPillChange,
  lookupFn,
  onPopulateInput,
  onOpenArtifact,
  isExpanded,
  onExpandToggle,
  onDownloadReport,
  hideHeader,
}) => {
  const dataframes = data.pandas_dataframes ?? [];
  const parents = data.parent_headers ?? [];
  // Derive inputsIndex from the persisted selectedInputName — falls back to 0 when
  // the name is empty or not found (e.g. first open, or parent list changed).
  const inputsIndex = useMemo(
    () =>
      Math.max(
        0,
        parents.findIndex(p => p.name === selectedInputName)
      ),
    [parents, selectedInputName]
  );
  const [isPopulatingInput, setIsPopulatingInput] = React.useState(false);

  const charts = useMemo(() => data.charts ?? [], [data]);

  // Build unified ordered list of result pills: dataframes → charts → reports.
  const resultPills = useMemo(
    (): ResultPill[] => [
      ...dataframes.map((df, i) => ({ kind: RESULT_PILL_KIND.dataframe, index: i, name: df.name! })),
      ...charts.map((c, i) => ({ kind: RESULT_PILL_KIND.chart, index: i, name: c.name! })),
      ...(data.reports ?? []).map((r, i) => ({ kind: RESULT_PILL_KIND.report, index: i, name: r.name! })),
    ],
    [dataframes, charts, data.reports]
  );

  // Match by name — no index math needed.
  const activePill: ResultPill | null =
    resultPills.find(p => p.name === selectedResultPillName) ?? resultPills[0] ?? null;
  const activeDf = activePill?.kind === RESULT_PILL_KIND.dataframe ? (dataframes[activePill.index] ?? null) : null;
  const activeChart = activePill?.kind === RESULT_PILL_KIND.chart ? (charts[activePill.index] ?? null) : null;
  const activeDocIndex = activePill?.kind === RESULT_PILL_KIND.report ? activePill.index : 0;

  // When the Inputs tab is active, check if the selected parent artifact needs population.
  // Fires on tab switch and on pill selection change.
  useEffect(() => {
    if (activeTab !== "inputs" || !onPopulateInput) return;
    const ph = parents[inputsIndex];
    if (!ph?.name) return;
    const artifact = lookupFn?.(ph.name);
    if (!artifact) return;
    const type = getArtifactType(artifact);
    // Determine if the parent artifact's data for this specific input (ph.name) is already loaded.
    let isAlreadyPopulated = false;
    if (type === ArtifactType.KG_DATAFRAME) {
      isAlreadyPopulated = !!artifact.kg_dataframe?.pandas_dataframe?.data_json;
    } else if (type === ArtifactType.DATAFRAME_ANALYSIS) {
      const dfa = artifact.dataframe_analysis;
      const dfPopulated = (dfa?.pandas_dataframes ?? []).some(d => d.name === ph.name && !!d.data_json);
      const chartPopulated = (dfa?.charts ?? []).some(c => c.name === ph.name && !!c.spec_json);
      const reportPopulated = (dfa?.reports ?? []).some(r => r.name === ph.name && !!r.pdf_bytes?.length);
      isAlreadyPopulated = dfPopulated || chartPopulated || reportPopulated;
    } else if (type === ArtifactType.FILE_ARTIFACT) {
      isAlreadyPopulated = !!artifact.file_artifact?.pandas_dataframe?.data_json;
    }
    const needsPopulation = !isAlreadyPopulated;
    if (needsPopulation) {
      setIsPopulatingInput(true);
      const p = onPopulateInput(artifact, { kind: getArtifactKind(artifact) });
      Promise.resolve(p).finally(() => setIsPopulatingInput(false));
    }
  }, [activeTab, inputsIndex, parents, lookupFn, onPopulateInput]);

  const { rows: numberOfRows, columns: numberOfColumns } = useMemo(
    () => getPandasDfRowColCount(activeDf ?? undefined),
    [activeDf]
  );

  // Row/col count for the currently selected parent in the Inputs tab.
  const inputsPh = parents[inputsIndex];
  const inputsArtifact = inputsPh?.name ? lookupFn?.(inputsPh.name) : undefined;

  const inputsPandasDf = useMemo(() => {
    if (!inputsArtifact) return null;
    const type = getArtifactType(inputsArtifact);
    if (type === ArtifactType.KG_DATAFRAME) return inputsArtifact.kg_dataframe?.pandas_dataframe ?? null;
    if (type === ArtifactType.DATAFRAME_ANALYSIS) {
      const ph = parents[inputsIndex];
      return (inputsArtifact.dataframe_analysis?.pandas_dataframes ?? []).find(d => d.name === ph?.name) ?? null;
    }
    if (type === ArtifactType.FILE_ARTIFACT) return inputsArtifact.file_artifact?.pandas_dataframe ?? null;
    return null;
  }, [inputsArtifact, parents, inputsIndex]);

  const inputsChart = useMemo(() => {
    if (!inputsArtifact) return null;
    const ph = parents[inputsIndex];
    return (inputsArtifact.dataframe_analysis?.charts ?? []).find(c => c.name === ph?.name) ?? null;
  }, [inputsArtifact, parents, inputsIndex]);

  const { rows: inputsRows, columns: inputsCols } = useMemo(
    () => getPandasDfRowColCount(inputsPandasDf ?? undefined),
    [inputsPandasDf]
  );

  // Single ref for whichever chart is currently rendered — only one tab is active at a time.
  const chartViewRef = React.useRef<VegaView | null>(null);

  // Single stable navigation callback for LineageGraphTab.
  // Receives the sub-resource name (or artifact header name) of the clicked node and
  // resolves where to navigate — keeping all routing logic in the parent where data
  // context (resultPills, parents, lookupFn) is available.
  const lineageNavigate = useCallback(
    (name: string, kind: string) => {
      // 1. Matches a result pill of the current artifact (charts, dataframes, reports)
      if (resultPills.find(p => p.name === name)) {
        onResultPillChange(name);
        onTabChange(TAB_ID.results);
        return;
      }
      // 2. Matches an input (parent header) of the current artifact
      const inputIdx = parents.findIndex(p => p.name === name);
      if (inputIdx !== -1) {
        onInputPillChange?.(name);
        onTabChange(TAB_ID.inputs);
        return;
      }
      // 3. Code node of the current artifact — its label IS the artifact's header name
      if (name === data.header?.name) {
        onTabChange(TAB_ID.code);
        return;
      }
      // 4. External artifact (KG source, upstream analysis) — open as a new panel tab.
      // kind is aligned with ARTIFACT_TAG_TYPE / RESULT_PILL_KIND so getArtifactOpenOpts
      // returns the correct opts directly (code→code tab, sub-resources→results+subResourceName).
      // kgSource falls through to getArtifactOpenOpts default (undefined opts) — KG dataframes
      // have no code/results sub-tabs so opening at the default tab is correct.
      const artifact = lookupFn?.(name);
      if (artifact) onOpenArtifact?.(artifact, getArtifactOpenOpts(kind, name));
    },
    [onTabChange, onResultPillChange, resultPills, parents, data.header?.name, lookupFn, onOpenArtifact]
  );

  const downloadChart = useCallback(async (format: "png" | "jpeg", name?: string) => {
    if (!chartViewRef.current) return;
    try {
      const url = await vegaViewToDataURL(chartViewRef.current, format);
      triggerDownload(url, `${name ?? "chart"}.${format === "jpeg" ? "jpg" : "png"}`);
    } catch {
      showToast("Download failed", "Could not export the chart. Please try again.", "error");
    }
    // chartViewRef is a stable ref — not a dep.
  }, []);

  const tabs = useMemo((): { id: TabId; label: string; icon: React.ElementType }[] => {
    const all: { id: TabId; label: string; icon: React.ElementType }[] = [
      { id: TAB_ID.results, label: "Results", icon: AppIcons.results },
      { id: TAB_ID.code, label: "Code", icon: AppIcons.codeAlt },
      ...(parents.length > 0 ? [{ id: TAB_ID.inputs, label: "Inputs", icon: AppIcons.inputs }] : []),
      { id: TAB_ID.trace, label: "Trace", icon: AppIcons.flowMerge },
    ];
    return all;
  }, [parents.length]);

  const getDownloadMenuForResult = () => {
    if (!activePill) return null;
    if (activePill.kind === RESULT_PILL_KIND.dataframe && activeDf)
      return (
        <DownloadMenu
          ariaLabel="Download"
          items={[
            {
              label: "Copy to clipboard",
              icon: "copy",
              onClick: () => copyRawTableToClipboard(extractFormattedDataFromPandasDf(activeDf)),
            },
            {
              label: "Download Excel file",
              icon: "fileExcel",
              onClick: () =>
                downloadRawTableAsExcel(
                  extractFormattedDataFromPandasDf(activeDf),
                  `${activeDf.name ?? "DataFrame"}.xlsx`
                ),
            },
          ]}
        />
      );
    if (activePill.kind === RESULT_PILL_KIND.chart && activeChart)
      return (
        <DownloadMenu
          ariaLabel="Download chart"
          items={[
            { label: "Download PNG", icon: "filePng", onClick: () => downloadChart("png", activeChart?.name) },
            { label: "Download JPG", icon: "fileJpg", onClick: () => downloadChart("jpeg", activeChart?.name) },
          ]}
        />
      );
    if (activePill.kind === RESULT_PILL_KIND.report)
      return (
        <DownloadMenu
          ariaLabel="Download report"
          items={[
            {
              label: "Download DOCX",
              icon: "document",
              onClick: () => onDownloadReport?.("docx", data.reports?.[activeDocIndex]?.name) ?? undefined,
            },
            {
              label: "Download PDF",
              icon: "fileExcel",
              onClick: () => onDownloadReport?.("pdf", data.reports?.[activeDocIndex]?.name) ?? undefined,
            },
          ]}
        />
      );
    return null;
  };

  const getDownloadMenuForInputs = () => {
    if (inputsPandasDf)
      return (
        <DownloadMenu
          ariaLabel="Download"
          items={[
            {
              label: "Copy to clipboard",
              icon: "copy",
              onClick: () => copyRawTableToClipboard(extractFormattedDataFromPandasDf(inputsPandasDf)),
            },
            {
              label: "Download Excel file",
              icon: "fileExcel",
              onClick: () =>
                downloadRawTableAsExcel(
                  extractFormattedDataFromPandasDf(inputsPandasDf),
                  `${inputsPandasDf.name ?? "input"}.xlsx`
                ),
            },
          ]}
        />
      );
    if (inputsChart)
      return (
        <DownloadMenu
          ariaLabel="Download chart"
          items={[
            { label: "Download PNG", icon: "filePng", onClick: () => downloadChart("png", inputsChart?.name) },
            { label: "Download JPG", icon: "fileJpg", onClick: () => downloadChart("jpeg", inputsChart?.name) },
          ]}
        />
      );
    return null;
  };

  const headerActions = (
    <>
      {/* Row / col count */}
      {activeTab === TAB_ID.results &&
        activePill?.kind === RESULT_PILL_KIND.dataframe &&
        numberOfRows > 0 &&
        numberOfColumns > 0 && (
          <Text fontSize="xs" color="gray.700" mx={2} mb={0}>
            {numberOfColumns} {numberOfColumns > 1 ? "cols" : "col"}, {numberOfRows} {numberOfRows > 1 ? "rows" : "row"}
          </Text>
        )}
      {activeTab === TAB_ID.inputs && inputsRows > 0 && inputsCols > 0 && (
        <Text fontSize="xs" color="gray.700" mx={2} mb={0}>
          {inputsCols} {inputsCols > 1 ? "cols" : "col"}, {inputsRows} {inputsRows > 1 ? "rows" : "row"}
        </Text>
      )}

      {/* Download */}
      {activeTab === TAB_ID.results && getDownloadMenuForResult()}
      {activeTab === TAB_ID.inputs && getDownloadMenuForInputs()}
    </>
  );

  return (
    <Box position="relative" display="flex" flexDirection="column" width="100%" flex="1 1 0" minH={0} overflow="hidden">
      <ArtifactPanelHeader
        icon={AppIcons.mathFunction}
        iconBg="white"
        iconColor="purple.700"
        name={data.header?.name ?? "Analysis"}
        typeLabel="Analysis"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={id => onTabChange(id as TabId)}
        actions={headerActions}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
        hideHeader={hideHeader}
      />

      <Box
        flex="1 1 0"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        overflowX="hidden"
        onWheel={e => e.stopPropagation()}
        sx={SCROLL_SX}
      >
        {/* ── Inputs Tab ── */}
        {activeTab === TAB_ID.inputs && (
          <InputsTab
            data={data}
            lookupFn={lookupFn ?? (() => undefined)}
            selectedIndex={inputsIndex}
            onSelectedIndexChange={i => onInputPillChange?.(parents[i]?.name ?? "")}
            isPopulatingInput={isPopulatingInput}
            onChartViewReady={view => {
              chartViewRef.current = view;
            }}
          />
        )}
        {activeTab === "inputs" && isPopulatingInput && (
          <Box
            p={2}
            position="absolute"
            top="15%"
            left="50%"
            transform="translate(-50%, -50%)"
            zIndex={25}
            bg="green.100"
            borderRadius={3}
            boxShadow="md"
          >
            <HStack spacing={2}>
              <Spinner size="sm" color="green.600" thickness="2px" />
              <Text mb={0} fontSize="xs" fontWeight="semibold" color="green.700">
                Loading artifact...
              </Text>
            </HStack>
          </Box>
        )}

        {/* ── Results Tab — unified pill bar for dataframes, charts, and docx reports ── */}
        {activeTab === TAB_ID.results && (
          <>
            {resultPills.length === 0 ? (
              <Box px={4} py={6} textAlign="center">
                <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                  Nothing to show. Please try again...
                </Text>
              </Box>
            ) : (
              <>
                {/* Pill bar */}
                <HStack
                  spacing={1.5}
                  px={2}
                  py={1.5}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  bg="white"
                  flexWrap="wrap"
                  position="sticky"
                  top={0}
                  zIndex={2}
                  boxShadow="sm"
                >
                  {resultPills.map(pill => {
                    const isActive = pill.name === activePill?.name;
                    const pillIcon =
                      pill.kind === RESULT_PILL_KIND.chart
                        ? AppIcons.chart
                        : pill.kind === RESULT_PILL_KIND.report
                          ? AppIcons.document
                          : AppIcons.dataframe;
                    return (
                      <Box
                        key={`${pill.kind}-${pill.index}`}
                        as="button"
                        type="button"
                        display="inline-flex"
                        alignItems="center"
                        px={3}
                        py={1}
                        borderRadius="md"
                        border={isActive ? "2px solid" : "1px solid"}
                        borderColor={isActive ? "purple.600" : "gray.300"}
                        bg="white"
                        color={isActive ? "purple.700" : "gray.600"}
                        fontSize="xs"
                        fontWeight={isActive ? "700" : "600"}
                        _hover={{
                          borderColor: "purple.400",
                          color: isActive ? "purple.700" : "purple.600",
                          bg: "purple.50",
                        }}
                        transition="all 0.15s"
                        cursor="pointer"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          onResultPillChange(pill.name);
                        }}
                      >
                        <HStack as="span" spacing={1.5} align="center">
                          <Icon as={pillIcon} boxSize={3} color="currentColor" flexShrink={0} aria-hidden />
                          <Text as="span" fontSize="xs" lineHeight="1.2" mb={0} isTruncated>
                            {pill.name}
                          </Text>
                        </HStack>
                      </Box>
                    );
                  })}
                </HStack>

                {/* Content area — switches on active pill kind */}
                {activePill?.kind === RESULT_PILL_KIND.dataframe &&
                  (activeDf ? (
                    <PandasTable df={activeDf} />
                  ) : (
                    <Box px={4} py={6} textAlign="center">
                      <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                        No dataframes available.
                      </Text>
                    </Box>
                  ))}
                {activePill?.kind === RESULT_PILL_KIND.chart && (
                  <Box w="100%" overflowX="auto" sx={CHART_SCROLL_SX}>
                    <AnalysisChartTab
                      chartVegaSpecJson={activeChart?.spec_json}
                      onViewReady={view => {
                        chartViewRef.current = view;
                      }}
                    />
                  </Box>
                )}
                {activePill?.kind === RESULT_PILL_KIND.report && <DocxTab report={data} reportIndex={activeDocIndex} />}
              </>
            )}
          </>
        )}

        {/* ── Trace Tab (Lineage Graph) — always mounted, toggled via CSS.
             Keeping the component mounted prevents React Flow from unmounting on
             every tab switch, which would destroy graph state and cause a rebuild
             flash each time the user returns to this tab. */}
        <Box
          flex="1 1 0"
          minH={0}
          overflow="hidden"
          display={activeTab === "trace" ? "flex" : "none"}
          flexDirection="column"
        >
          <LineageGraphTab
            data={data}
            lookupFn={lookupFn}
            isActive={activeTab === "trace"}
            onNavigate={lineageNavigate}
          />
        </Box>

        {/* ── Code Tab ── */}
        {activeTab === TAB_ID.code && <CodeTab code={data.code} />}
      </Box>
    </Box>
  );
};

export default DataFrameAnalysis;
